import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronLeft, X, User, Mail, Phone, Building, DollarSign, Clock, Target, MessageSquare, Check, ArrowRight } from 'lucide-react';


const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:5001';

const EnhancedInvestorSurvey = ({ showSurvey, setShowSurvey, language = 'en' }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    investmentRange: '',
    timeline: '',
    experience: '',
    interests: [],
    accreditedInvestor: '',
    preferredContact: '',
    message: ''
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState('forward');
  const inputRef = useRef(null);

  const investmentTypes = language === 'en' 
    ? ['Residential Development', 'Commercial Real Estate', 'Mixed-Use Projects', 'Sustainable Development', 'Land Acquisition', 'Joint Ventures']
    : ['Desarrollo Residencial', 'Bienes Raíces Comerciales', 'Proyectos de Uso Mixto', 'Desarrollo Sostenible', 'Adquisición de Terrenos', 'Joint Ventures'];

  const questions = [
    {
      id: 'name',
      type: 'text',
      title: language === 'en' ? "What's your name?" : "¿Cuál es tu nombre?",
      subtitle: language === 'en' ? "(First and last, please.)" : "(Nombre y apellido, por favor.)",
      placeholder: language === 'en' ? "Type your answer here..." : "Escribe tu respuesta aquí...",
      required: true,
      icon: User
    },
    {
      id: 'email',
      type: 'email',
      title: language === 'en' ? "What's your email address?" : "¿Cuál es tu correo electrónico?",
      subtitle: language === 'en' ? "We'll use this to send you investment opportunities." : "Lo usaremos para enviarte oportunidades de inversión.",
      placeholder: language === 'en' ? "name@example.com" : "nombre@ejemplo.com",
      required: true,
      icon: Mail
    },
    {
      id: 'phone',
      type: 'tel',
      title: language === 'en' ? "What's your phone number?" : "¿Cuál es tu número de teléfono?",
      subtitle: language === 'en' ? "Optional, but helpful for urgent opportunities." : "Opcional, pero útil para oportunidades urgentes.",
      placeholder: language === 'en' ? "+1 (555) 123-4567" : "+1 (555) 123-4567",
      required: false,
      icon: Phone
    },
    {
      id: 'company',
      type: 'text',
      title: language === 'en' ? "What company or organization are you with?" : "¿Con qué empresa u organización estás?",
      subtitle: language === 'en' ? "This helps us understand your background." : "Esto nos ayuda a entender tu experiencia.",
      placeholder: language === 'en' ? "Your company name..." : "Nombre de tu empresa...",
      required: false,
      icon: Building
    },
    {
      id: 'investmentRange',
      type: 'select',
      title: language === 'en' ? "What's your investment range?" : "¿Cuál es tu rango de inversión?",
      subtitle: language === 'en' ? "This helps us match you with suitable opportunities." : "Esto nos ayuda a emparejarte con oportunidades adecuadas.",
      options: [
        { value: '100k-500k', label: '$100K - $500K' },
        { value: '500k-1m', label: '$500K - $1M' },
        { value: '1m-5m', label: '$1M - $5M' },
        { value: '5m+', label: '$5M+' }
      ],
      required: true,
      icon: DollarSign
    },
    {
      id: 'timeline',
      type: 'select',
      title: language === 'en' ? "What's your investment timeline?" : "¿Cuál es tu cronograma de inversión?",
      subtitle: language === 'en' ? "When are you looking to invest?" : "¿Cuándo estás buscando invertir?",
      options: [
        { 
          value: 'immediate', 
          label: language === 'en' ? 'Immediate (0-3 months)' : 'Inmediato (0-3 meses)' 
        },
        { 
          value: 'short', 
          label: language === 'en' ? 'Short-term (3-6 months)' : 'Corto plazo (3-6 meses)' 
        },
        { 
          value: 'medium', 
          label: language === 'en' ? 'Medium-term (6-12 months)' : 'Mediano plazo (6-12 meses)' 
        },
        { 
          value: 'long', 
          label: language === 'en' ? 'Long-term (12+ months)' : 'Largo plazo (12+ meses)' 
        }
      ],
      required: false,
      icon: Clock
    },
    {
      id: 'experience',
      type: 'select',
      title: language === 'en' ? "What's your investment experience?" : "¿Cuál es tu experiencia de inversión?",
      subtitle: language === 'en' ? "This helps us provide appropriate guidance." : "Esto nos ayuda a proporcionar orientación apropiada.",
      options: [
        { 
          value: 'beginner', 
          label: language === 'en' ? 'Beginner (0-2 years)' : 'Principiante (0-2 años)' 
        },
        { 
          value: 'intermediate', 
          label: language === 'en' ? 'Intermediate (2-5 years)' : 'Intermedio (2-5 años)' 
        },
        { 
          value: 'experienced', 
          label: language === 'en' ? 'Experienced (5+ years)' : 'Experimentado (5+ años)' 
        }
      ],
      required: false,
      icon: Target
    },
    {
      id: 'interests',
      type: 'multiselect',
      title: language === 'en' ? "What types of investments interest you?" : "¿Qué tipos de inversiones te interesan?",
      subtitle: language === 'en' ? "Select all that apply." : "Selecciona todos los que apliquen.",
      options: investmentTypes.map(type => ({ value: type, label: type })),
      required: false,
      icon: Target
    },
    {
      id: 'accreditedInvestor',
      type: 'select',
      title: language === 'en' ? "Are you an accredited investor?" : "¿Eres un inversionista acreditado?",
      subtitle: language === 'en' ? "This affects which opportunities we can share." : "Esto afecta qué oportunidades podemos compartir.",
      options: [
        { 
          value: 'yes', 
          label: language === 'en' ? 'Yes, I am an accredited investor' : 'Sí, soy un inversionista acreditado' 
        },
        { 
          value: 'no', 
          label: language === 'en' ? 'No, I am not an accredited investor' : 'No, no soy un inversionista acreditado' 
        },
        { 
          value: 'unsure', 
          label: language === 'en' ? "I'm not sure" : 'No estoy seguro' 
        }
      ],
      required: false,
      icon: Check
    },
    {
      id: 'preferredContact',
      type: 'select',
      title: language === 'en' ? "How do you prefer to be contacted?" : "¿Cómo prefieres que te contactemos?",
      subtitle: language === 'en' ? "We'll respect your preference." : "Respetaremos tu preferencia.",
      options: [
        { value: 'email', label: language === 'en' ? 'Email' : 'Correo Electrónico' },
        { value: 'phone', label: language === 'en' ? 'Phone Call' : 'Llamada Telefónica' },
        { value: 'video', label: language === 'en' ? 'Video Conference' : 'Videoconferencia' },
        { value: 'meeting', label: language === 'en' ? 'In-Person Meeting' : 'Reunión en Persona' }
      ],
      required: false,
      icon: MessageSquare
    },
    {
      id: 'message',
      type: 'textarea',
      title: language === 'en' ? "Anything else you'd like us to know?" : "¿Algo más que te gustaría que sepamos?",
      subtitle: language === 'en' ? "Investment goals, questions, or special requirements." : "Objetivos de inversión, preguntas o requisitos especiales.",
      placeholder: language === 'en' 
        ? "Tell us about your investment goals, risk tolerance, or any questions you have..."
        : "Cuéntanos sobre tus objetivos de inversión, tolerancia al riesgo o cualquier pregunta que tengas...",
      required: false,
      icon: MessageSquare
    }
  ];

  const currentQuestionData = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  useEffect(() => {
    if (showSurvey && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 300);
    }
  }, [currentQuestion, showSurvey]);

  const handleNext = () => {
    if (currentQuestionData.required && !formData[currentQuestionData.id]) {
      return;
    }

    if (currentQuestion < questions.length - 1) {
      setDirection('forward');
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
        setIsAnimating(false);
      }, 150);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setDirection('backward');
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentQuestion(prev => prev - 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && currentQuestionData.type !== 'textarea') {
      e.preventDefault();
      handleNext();
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/survey/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
  
      if (!res.ok) throw new Error(`Server responded ${res.status}`);
  
      // ✅ success – show thank-you message
      alert(
        language === 'en'
          ? 'Thank you! Your profile was received. We’ll be in touch soon.'
          : '¡Gracias! Hemos recibido tu perfil. Nos pondremos en contacto pronto.'
      );
  
      // reset & close modal
      setShowSurvey(false);
      setCurrentQuestion(0);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        investmentRange: '',
        timeline: '',
        experience: '',
        interests: [],
        accreditedInvestor: '',
        preferredContact: '',
        message: '',
      });
    } catch (err) {
      console.error(err);
      alert(
        language === 'en'
          ? 'Submission failed – please try again in a moment.'
          : 'No se pudo enviar – inténtalo de nuevo en un momento.'
      );
    }
  };

  const isValid = () => {
    if (currentQuestionData.required) {
      return formData[currentQuestionData.id] && formData[currentQuestionData.id].length > 0;
    }
    return true;
  };

  const renderQuestionInput = () => {
    const IconComponent = currentQuestionData.icon;

    switch (currentQuestionData.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <div className="relative">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400">
              <IconComponent size={20} />
            </div>
            <input
              ref={inputRef}
              type={currentQuestionData.type}
              value={formData[currentQuestionData.id] || ''}
              onChange={(e) => handleFormChange(currentQuestionData.id, e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={currentQuestionData.placeholder}
              className="w-full pl-12 pr-4 py-4 bg-transparent border-b-2 border-gray-600 focus:border-blue-400 outline-none text-xl text-white placeholder-gray-400 transition-all duration-300"
              required={currentQuestionData.required}
            />
          </div>
        );

      case 'textarea':
        return (
          <div className="relative">
            <div className="absolute left-4 top-6 text-blue-400">
              <IconComponent size={20} />
            </div>
            <textarea
              ref={inputRef}
              rows={4}
              value={formData[currentQuestionData.id] || ''}
              onChange={(e) => handleFormChange(currentQuestionData.id, e.target.value)}
              placeholder={currentQuestionData.placeholder}
              className="w-full pl-12 pr-4 py-4 bg-transparent border-b-2 border-gray-600 focus:border-blue-400 outline-none text-xl text-white placeholder-gray-400 resize-none transition-all duration-300"
            />
          </div>
        );

      case 'select':
        return (
          <div className="space-y-3">
            {currentQuestionData.options.map((option, index) => (
              <button
                key={option.value}
                onClick={() => handleFormChange(currentQuestionData.id, option.value)}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-300 hover:border-blue-400 hover:bg-blue-400/10 ${
                  formData[currentQuestionData.id] === option.value
                    ? 'border-blue-400 bg-blue-400/20 text-white'
                    : 'border-gray-600 text-gray-300'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full border-2 ${
                    formData[currentQuestionData.id] === option.value
                      ? 'border-blue-400 bg-blue-400'
                      : 'border-gray-400'
                  }`}></div>
                  <span className="text-lg">{option.label}</span>
                </div>
              </button>
            ))}
          </div>
        );

      case 'multiselect':
        return (
          <div className="space-y-3">
            {currentQuestionData.options.map((option, index) => (
              <button
                key={option.value}
                onClick={() => handleInterestToggle(option.value)}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-300 hover:border-blue-400 hover:bg-blue-400/10 ${
                  formData.interests.includes(option.value)
                    ? 'border-blue-400 bg-blue-400/20 text-white'
                    : 'border-gray-600 text-gray-300'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    formData.interests.includes(option.value)
                      ? 'border-blue-400 bg-blue-400'
                      : 'border-gray-400'
                  }`}>
                    {formData.interests.includes(option.value) && (
                      <Check size={12} className="text-white" />
                    )}
                  </div>
                  <span className="text-lg">{option.label}</span>
                </div>
              </button>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  if (!showSurvey) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-4">
              <h3 className="text-2xl font-bold text-white">
                {language === 'en' ? 'Invest In' : 'Invertir En'}
              </h3>
              <div className="text-sm text-gray-400">
                {currentQuestion + 1} {language === 'en' ? 'of' : 'de'} {questions.length}
              </div>
            </div>
            <button 
              onClick={() => setShowSurvey(false)}
              className="text-gray-400 hover:text-white text-2xl"
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Content */}
        <div className="p-8 min-h-[400px] flex flex-col justify-center">
          <div className={`transition-all duration-300 ${
            isAnimating 
              ? direction === 'forward' 
                ? 'opacity-0 transform translate-x-8' 
                : 'opacity-0 transform -translate-x-8'
              : 'opacity-100 transform translate-x-0'
          }`}>
            {/* Question Number and Icon */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                <currentQuestionData.icon size={24} className="text-white" />
              </div>
              <span className="text-blue-400 text-lg font-medium">
                {currentQuestion + 1} → 
              </span>
            </div>

            {/* Question Title */}
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              {currentQuestionData.title}
            </h2>

            {/* Question Subtitle */}
            {currentQuestionData.subtitle && (
              <p className="text-xl text-gray-400 mb-8">
                {currentQuestionData.subtitle}
              </p>
            )}

            {/* Question Input */}
            <div className="mb-8">
              {renderQuestionInput()}
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="p-6 border-t border-gray-700 flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-300 ${
              currentQuestion === 0
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-gray-300 hover:text-white hover:bg-gray-800'
            }`}
          >
            <ChevronLeft size={20} />
            <span>{language === 'en' ? 'Previous' : 'Anterior'}</span>
          </button>

          <div className="flex items-center space-x-4">
            {/* Enter hint for keyboard users */}
            <span className="text-xs text-gray-500 hidden md:block">
              {language === 'en' ? 'Press Enter ↵' : 'Presiona Enter ↵'}
            </span>

            <button
              onClick={handleNext}
              disabled={currentQuestionData.required && !isValid()}
              className={`flex items-center space-x-2 px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
                currentQuestionData.required && !isValid()
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-105'
              }`}
            >
              <span>
                {currentQuestion === questions.length - 1
                  ? language === 'en' ? 'Submit' : 'Enviar'
                  : language === 'en' ? 'Continue' : 'Continuar'
                }
              </span>
              {currentQuestion === questions.length - 1 ? (
                <Check size={20} />
              ) : (
                <ArrowRight size={20} />
              )}
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="px-6 pb-6">
          <p className="text-xs text-gray-500 leading-relaxed">
            {language === 'en'
              ? 'This form is not an offer of securities or a general solicitation. Your indication of interest is non-binding.'
              : 'Este formulario no es una oferta de valores o una solicitud general. Tu indicación de interés no es vinculante.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EnhancedInvestorSurvey;